import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/src/store";
import { hydrateAccessFromStorage } from "@/src/store/slices/AccessControl/accessControlSlice";
import { setActualOfficeTable as setApplicationsOffice } from "@/src/store/slices/ApplicationPage/applicationsFiltersSlice";
import { setActualOfficeTable as setTasksOffice } from "@/src/store/slices/TasksPage/tasksFiltersSlice";
import { setActualOfficeTable as setOfficeAccountingOffice } from "@/src/store/slices/OfficeAccountingPage/officeAccountingFiltersSlice";
import { resolveSyncedTableOffice } from "@/src/constants/accessControl";

/**
 * Bootstraps accessControl from localStorage and syncs table `office`
 * when the current value is outside the allowed set (role and/or selected office_url).
 * Does not reset a valid office, and does not force ALL when the user has full access.
 */
const AccessControlBootstrap = () => {
  const dispatch = useDispatch<AppDispatch>();
  const hasFullCountryAccess = useSelector((state: RootState) => state.accessControl.hasFullCountryAccess);
  const allowedOffices = useSelector((state: RootState) => state.accessControl.allowedOffices);
  const applicationsOffice = useSelector((state: RootState) => state.applicationsFilters.actualOfficeTable);
  const tasksOffice = useSelector((state: RootState) => state.tasksFilters.actualOfficeTable);
  const officeAccountingOffice = useSelector((state: RootState) => state.officeAccountingFilters.actualOfficeTable);

  useEffect(() => {
    dispatch(hydrateAccessFromStorage());
  }, [dispatch]);

  useEffect(() => {
    const nextApplications = resolveSyncedTableOffice(applicationsOffice, hasFullCountryAccess, allowedOffices);
    const nextTasks = resolveSyncedTableOffice(tasksOffice, hasFullCountryAccess, allowedOffices);
    const nextAccounting = resolveSyncedTableOffice(
      officeAccountingOffice,
      hasFullCountryAccess,
      allowedOffices,
    );

    if (nextApplications) dispatch(setApplicationsOffice(nextApplications));
    if (nextTasks) dispatch(setTasksOffice(nextTasks));
    if (nextAccounting) dispatch(setOfficeAccountingOffice(nextAccounting));
  }, [
    allowedOffices,
    applicationsOffice,
    dispatch,
    hasFullCountryAccess,
    officeAccountingOffice,
    tasksOffice,
  ]);

  return null;
};

export default AccessControlBootstrap;
